import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import store, { IAppState } from "../../state/Store";
import Datastore from "../../communication/Datastore";
import School from "../../types/core/School";
import TwoFactorConfiguration from "../../types/twoFactor/TwoFactorConfig";
import Address from "../../types/core/Address";
import Timezone from "../../types/core/Timezone";
import { Migration } from "../../types/core/Migration";

// Define the type for our state store entry
type SchoolState = {
    school: School;
    schools: School[];
    address: Address;
    timezone: Timezone;
    migration: Migration;
    status: string;
    error: string;
    twoFactor: TwoFactorConfiguration;
    twoFactorStatus: string;
    timezoneStatus: string;
    addressStatus: string;
    migrationStatus: string;
};

// Define the initial state for our state store entry
const initialState: SchoolState = {
    school: null,
    schools: [],
    address: null,
    timezone: null,
    migration: null,
    status: "idle",
    error: null,
    twoFactor: null,
    twoFactorStatus: "idle",
    timezoneStatus: "idle",
    addressStatus: "idle",
    migrationStatus: "idle",
};

// Create a slice to hold all our reducers
const schoolSlice = createSlice({
    name: "school",
    initialState: initialState,
    reducers: {
        updateSchoolTwoFactorValue: (
            state,
            action: PayloadAction<TwoFactorConfiguration>,
        ) => {
            state.twoFactor = action.payload;
        },
        clearStatus: state => {
            state.status = "idle";
            state.addressStatus = "idle";
            state.timezoneStatus = "idle";
            state.twoFactorStatus = "idle";
            state.migrationStatus = "idle";
            state.error = null;
        },
        clearTimezone: state => {
            state.timezone = null;
            state.timezoneStatus = "idle";
        },
        clearMigration: state => {
            state.migration = null;
            state.migrationStatus = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;

                const school = rawData?.payload?.school?.school
                    ? new School(rawData?.payload?.school.school)
                    : null;
                const schools = rawData?.payload?.school?.schools
                    ? rawData?.payload?.school?.schools.map(
                          school => new School(school),
                      )
                    : [];

                return {
                    school: school,
                    schools: schools,
                    address: rawData?.payload?.school?.address
                        ? new Address(rawData?.payload?.school.address)
                        : null,
                    timezone: rawData?.payload?.school?.timezone,
                    migration: rawData?.payload?.school?.migration,
                    status:
                        !school || !schools || !schools.length
                            ? "idle"
                            : "succeeded",
                    error: null,
                    twoFactor: rawData?.payload?.school?.twoFactor,
                    twoFactorStatus: !rawData?.payload?.school?.twoFactor
                        ? "idle"
                        : "succeeded",
                    addressStatus: !rawData?.payload?.school?.address
                        ? "idle"
                        : "succeeded",
                    timezoneStatus: !rawData?.payload?.school?.timezone
                        ? "idle"
                        : "succeeded",
                    migrationStatus: !rawData?.payload?.school?.migration
                        ? "idle"
                        : "succeeded",
                };
            })
            .addCase(fetchSchool.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchSchool.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.school = new School(action.payload[0]);
                state.schools = action.payload[1].map(
                    school => new School(school),
                );
            })
            .addCase(fetchSchool.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateSchool.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateSchool.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.school = new School(action.payload[0]);
            })
            .addCase(updateSchool.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchTimezone.pending, (state, action) => {
                state.timezoneStatus = "loading";
            })
            .addCase(fetchTimezone.fulfilled, (state, action) => {
                state.timezoneStatus = "succeeded";
                // Add any fetched posts to the array
                state.timezone = action.payload;
            })
            .addCase(fetchTimezone.rejected, (state, action) => {
                state.timezoneStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchSchoolAddress.pending, (state, action) => {
                state.addressStatus = "loading";
            })
            .addCase(fetchSchoolAddress.fulfilled, (state, action) => {
                state.addressStatus = "succeeded";
                // Add any fetched posts to the array
                state.address = action.payload[0];
            })
            .addCase(fetchSchoolAddress.rejected, (state, action) => {
                state.addressStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(updateSchoolAddress.pending, (state, action) => {
                state.addressStatus = "loading";
            })
            .addCase(updateSchoolAddress.fulfilled, (state, action) => {
                state.addressStatus = "succeeded";
                // Add any fetched posts to the array
                state.address = action.payload[0];
            })
            .addCase(updateSchoolAddress.rejected, (state, action) => {
                state.addressStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchSchool2FA.pending, (state, action) => {
                state.twoFactorStatus = "loading";
            })
            .addCase(fetchSchool2FA.fulfilled, (state, action) => {
                state.twoFactorStatus = "succeeded";
                // Add any fetched posts to the array
                state.twoFactor = action.payload;
            })
            .addCase(fetchSchool2FA.rejected, (state, action) => {
                state.twoFactorStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchSchoolMigration.pending, (state, action) => {
                state.migrationStatus = "loading";
            })
            .addCase(fetchSchoolMigration.fulfilled, (state, action) => {
                state.migrationStatus = "succeeded";
                // Add any fetched posts to the array
                state.migration = action.payload;
            })
            .addCase(fetchSchoolMigration.rejected, (state, action) => {
                state.migrationStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(enactSchoolMigration.pending, (state, action) => {
                state.migrationStatus = "loading";
            })
            .addCase(enactSchoolMigration.fulfilled, (state, action) => {
                state.migrationStatus = "succeeded";
                // Add any fetched posts to the array
                state.migration = action.payload;
            })
            .addCase(enactSchoolMigration.rejected, (state, action) => {
                state.migrationStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const {
    updateSchoolTwoFactorValue,
    clearStatus,
    clearTimezone,
    clearMigration,
} = schoolSlice.actions;

// This is a thunk, they are used to load the state
export const initialiseSchoolDetails = createAsyncThunk(
    "school/initialiseSchoolDetails",
    async () => {
        await Promise.all([
            store.dispatch(fetchSchool()),
            store.dispatch(fetchTimezone()),
            store.dispatch(fetchSchool2FA()),
        ]);
        store.dispatch(fetchSchoolAddress());
    },
    {
        condition: (_, api) => {
            const state = store.getState() as IAppState;
            return (
                state.db.school.status === "idle" &&
                state.db.school.timezoneStatus === "idle" &&
                state.db.school.twoFactorStatus === "idle"
            );
        },
    },
);
export const reinitialiseSchoolDetails = createAsyncThunk(
    "school/reinitialiseSchoolDetails",
    async (_, thunkAPI) => {
        thunkAPI.dispatch(clearStatus());
        thunkAPI.dispatch(initialiseSchoolDetails());
    },
    {
        condition: (_, api) => store.getState().db.school.status !== "loading",
    },
);

export const fetchSchool = createAsyncThunk(
    "school/fetchSchool",
    () =>
        Promise.all([
            Datastore.getType<School>("api/Authentication/Session/School"),
            Datastore.getType<School[]>("api/Authentication/Session/Schools"),
        ]),
    {
        condition: (_, api) => store.getState().db.school.status === "idle",
    },
);
export const updateSchool = createAsyncThunk(
    "school/updateSchoolDetails",
    async (payload: School, thunkAPI) => {
        const result = await Datastore.putType<School[]>("api/Schools", [
            payload,
        ]);
        thunkAPI.dispatch(clearTimezone());
        thunkAPI.dispatch(fetchTimezone());
        return result;
    },
    {
        condition: (_, api) =>
            store.getState().db.school.status === "succeeded",
    },
);

export const fetchTimezone = createAsyncThunk(
    "school/fetchTimezone",
    () => {
        return Datastore.getType<Timezone>(
            "api/Authentication/CurrentTimezone",
        );
    },
    {
        condition: (_, api) =>
            store.getState().db.school.timezoneStatus === "idle",
    },
);

export const fetchSchoolAddress = createAsyncThunk(
    "school/fetchSchoolAddress",
    () => {
        const state = store.getState() as IAppState;
        return Datastore.getType<Address[]>(
            `api/Addresses/Schools/${state.db.school.school.id}`,
        );
    },
    {
        condition: (_, api) => {
            const school = store.getState().db.school;
            return (
                school.status === "succeeded" && school.addressStatus === "idle"
            );
        },
    },
);
export const updateSchoolAddress = createAsyncThunk(
    "school/updateSchoolAddress",
    (payload: Address) =>
        Datastore.putType<Address[]>("api/Addresses", [payload]),
    {
        condition: (_, api) =>
            store.getState().db.school.addressStatus === "succeeded",
    },
);

export const fetchSchool2FA = createAsyncThunk(
    "school/fetchSchool2FA",
    () =>
        Datastore.getType<TwoFactorConfiguration>(
            "api/Authentication/Session/TwoFactorSettings",
        ),
    {
        condition: (_, api) =>
            store.getState().db.school.twoFactorStatus === "idle",
    },
);
export const updateSchool2FA = createAsyncThunk(
    "school/updateSchool2FA",
    (payload: TwoFactorConfiguration, thunkAPI) => {
        Datastore.post("api/Schools/TwoFactorSettings", payload);
        thunkAPI.dispatch(updateSchoolTwoFactorValue(payload));
    },
    {
        condition: (_, api) =>
            store.getState().db.school.twoFactorStatus === "idle",
    },
);

export const fetchSchoolMigration = createAsyncThunk(
    "school/fetchSchoolMigration",
    () => Datastore.getType<Migration>("api/Schools/Migration"),
    {
        condition: (_, api) =>
            store.getState().db.school.migrationStatus === "idle",
    },
);
export const enactSchoolMigration = createAsyncThunk(
    "school/enactSchoolMigration",
    async (payload: Migration) => {
        await Datastore.post("api/Schools/Migrate", payload);
        return payload;
    },
    {
        condition: (_, api) =>
            store.getState().db.school.migrationStatus === "succeeded",
    },
);
export const cancelSchoolMigration = createAsyncThunk(
    "school/cancelSchoolMigration",
    async (_, thunkAPI) => {
        await Datastore.get("api/Schools/Migration/Cancel");
        thunkAPI.dispatch(clearMigration());
        thunkAPI.dispatch(fetchSchoolMigration());
    },
    {
        condition: (_, api) =>
            store.getState().db.school.migrationStatus === "succeeded",
    },
);

// We have to export the reducer as this is added to the state store
export default schoolSlice.reducer;
